import { PspRules } from "@CommonTypes/backend/PspRules";
import { PALLET_DEFAULT_MAX_SKUS } from "@CommonUtils/pog/pallet";
import { Flex, HorizontalDivider, NumberInput, Text } from "src/elements";
import { useTranslation } from "src/i18n";
import { DIMENSION } from "src/utils/formatDimension";

import { RulesSection } from "./components/RulesSection";
import { RuleSelect } from "./components/RuleSelect";

type Rules = NonNullable<PspRules["pallets"]>;

interface Props {
  isDisabled?: boolean;

  /** 1 to PALLET_DEFAULT_MAX_SKUS; pogger's `max_skus` on every pallet. */
  maxProductsPerPallet: Rules["max_products_per_pallet"];
  setMaxProductsPerPallet: (value: Rules["max_products_per_pallet"]) => void;

  /** Fixture-geometry units (hundredths of an inch); null is each pallet's own height. */
  maxStackHeight: Rules["max_stack_height"];
  setMaxStackHeight: (value: Rules["max_stack_height"]) => void;
}

/**
 * The stack height to keep for inches typed: hundredths of an inch, or null
 * (each pallet's own height) for an emptied box, 0, or anything that rounds to
 * 0. Rounded first, so a tiny value is never kept as 0, which import refuses.
 */
export const stackHeightFromInches = (inches: number): Rules["max_stack_height"] => {
  const height = Math.round(inches * DIMENSION.INCH);
  return height > 0 ? height : null;
};

const PRODUCTS_PER_PALLET_OPTIONS = Array.from({ length: PALLET_DEFAULT_MAX_SKUS }, (_, index) => ({
  label: String(index + 1),
  value: index + 1,
}));

export const PalletRules = ({
  isDisabled,
  maxProductsPerPallet,
  setMaxProductsPerPallet,
  maxStackHeight,
  setMaxStackHeight,
}: Props) => {
  const { t } = useTranslation("common");

  return (
    <RulesSection title={t("planogramRules.pallets.title")} area="pallets" isDisabled={isDisabled}>
      <Flex padding="10px 10px 0" justify="center">
        <Text variant="caption1" textAlign="center" isDisabled={isDisabled}>
          {t("planogramRules.pallets.onlyPalletStores")}
        </Text>
      </Flex>

      <RuleSelect
        title={t("planogramRules.pallets.maxProducts")}
        options={PRODUCTS_PER_PALLET_OPTIONS}
        value={maxProductsPerPallet}
        setValue={setMaxProductsPerPallet}
        isDisabled={isDisabled}
      />

      <HorizontalDivider />

      <Flex column padding="10px" gap="10px">
        {/* Inches on screen, the planogram's hundredths of an inch on the wire,
            as the shelf editor converts. Emptied, or rounding to 0, is no cap: null. */}
        <NumberInput
          name="pallet-max-stack-height"
          title={t("planogramRules.pallets.maxStackHeight")}
          value={maxStackHeight === null ? 0 : maxStackHeight / DIMENSION.INCH}
          setValue={(inches) => setMaxStackHeight(stackHeightFromInches(inches))}
          min={0}
          isDisabled={isDisabled}
        />

        <Text variant="caption1" isDisabled={isDisabled}>
          {t("planogramRules.pallets.maxStackHeightHelp")}
        </Text>
      </Flex>
    </RulesSection>
  );
};
